// Assignments manager — table of client sub-accounts (location) → assigned agency
// user. "Unassigned (generic pot)" is the null option. Saved via PUT /assignments.
import { useEffect, useState } from 'react';
import type { AgencyUser, ClientAssignment } from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { Button, Card, EmptyNote, ErrorNote, Select, Spinner } from './ui';

const UNASSIGNED = '__pot__';

export default function Assignments({ users }: { users: AgencyUser[] }) {
  const [rows, setRows] = useState<ClientAssignment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    agencyApi
      .assignments()
      .then((r) => setRows(r))
      .catch((e: Error) => setError(e.message));
  }, []);

  function setUser(locationId: string, raw: string) {
    if (!rows) return;
    const value = raw === UNASSIGNED ? null : raw;
    setRows(rows.map((r) => (r.location_id === locationId ? { ...r, agency_user_id: value } : r)));
    setDirty(true);
    setSaved(false);
  }

  async function save() {
    if (!rows) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await agencyApi.saveAssignments(rows);
      if (updated.length) setRows(updated);
      setDirty(false);
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (error) return <ErrorNote error={error} />;
  if (!rows) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Route new tickets from each client sub-account to a team member. New tickets from an unassigned
          client land in the generic pot.
        </p>
        <div className="flex items-center gap-3">
          {saved && !dirty && <span className="text-xs text-emerald-600">Saved</span>}
          <Button onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save assignments'}
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyNote>No client sub-accounts yet. They appear here once connected.</EmptyNote>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="px-4 py-3 font-medium">Location ID</th>
                <th className="px-4 py-3 font-medium">Assigned to</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.location_id}>
                  <td className="px-4 py-3 font-medium text-slate-800">{r.client_name ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{r.location_id}</td>
                  <td className="px-4 py-3">
                    <Select
                      value={r.agency_user_id ?? UNASSIGNED}
                      onChange={(v) => setUser(r.location_id, v)}
                    >
                      <option value={UNASSIGNED}>Unassigned (generic pot)</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
